<template>
  <nav class="top-bar-navigation" ref="navigationContainer">
    <div class="top-bar-navigation__visible-items" ref="visibleItemsContainer">
      <VcNavItem
        v-for="item in visibleItems"
        :key="item.uid"
        :mode="navItemMode"
        :label="getItemTitle(item)"
        :is-selected="item.uid === activeItemUid"
        :is-external="isItemExternal(item)"
        :data-qa="`nav-item-${item.key}`"
        @click="handleItemClick(item)"
      />
    </div>

    <VcMenu
      v-if="overflowItems.length > 0"
      v-model="isMoreMenuOpen"
      class="top-bar-navigation__more-menu"
      offset-y
      left
    >
      <template #activator="{ on, attrs }">
        <VcNavItem
          ref="moreButton"
          :label="$t('common.navigation.more')"
          :is-selected="hasActiveInOverflow"
          :mode="navItemMode"
          icon="icon-chevron-down"
          :icon-size="16"
          data-qa="nav-item-more"
          v-bind="attrs"
          v-on="on"
        />
      </template>
      <VcList
        :items="overflowListItems"
        data-qa="nav-overflow-list"
        @select="handleOverflowItemSelect"
      />
    </VcMenu>
  </nav>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { isExternalUrl } from '../utils/navigationUtils';
import { isActionExternal } from '../utils/actionExecutor';

export default {
  name: 'TopBarNavigation',
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    activeItemUid: {
      type: String,
      default: null,
    },
    locale: {
      type: String,
      default: 'en',
    },
  },
  data() {
    return {
      visibleCount: 0,
      resizeObserver: null,
      itemWidths: [],
      moreButtonWidth: 80,
      isMoreMenuOpen: false,
    };
  },
  computed: {
    ...mapGetters('PlatformNavigationStore', ['getItemTitle']),
    ...mapGetters('SearchBarStore', { hiddenBySearch: 'showSearchResults' }),
    ...mapGetters('BusinessStore', ['textColor']),
    navItemMode() {
      return this.textColor === '#ffffff' ? 'dark' : 'light';
    },
    visibleItems() {
      if (this.visibleCount === 0 || this.visibleCount >= this.items.length) {
        return this.items;
      }
      return this.items.slice(0, this.visibleCount);
    },
    overflowItems() {
      if (this.visibleCount === 0 || this.visibleCount >= this.items.length) {
        return [];
      }
      return this.items.slice(this.visibleCount);
    },
    hasActiveInOverflow() {
      return this.overflowItems.some((item) => item.uid === this.activeItemUid);
    },
    /**
     * Transform overflow items into VcList items format
     * VcList expects items with identifier (number) and label properties
     */
    overflowListItems() {
      return this.overflowItems.map((item, index) => ({
        identifier: index,
        label: this.getItemTitle(item),
        selected: item.uid === this.activeItemUid,
        // Store original item reference for click handler
        originalItem: item,
      }));
    },
  },
  mounted() {
    this.initResizeObserver();
    // Defer initial calculation until after flex layout has run (nav gets its real width)
    this.$nextTick(() => {
      requestAnimationFrame(() => {
        this.calculateVisibleItems();
      });
    });
    // Listen for overlay click to close the menu
    this.$root.$on('overlay-clicked', this.onOverlayClicked);
  },
  beforeDestroy() {
    this.destroyResizeObserver();
    this.$root.$off('overlay-clicked', this.onOverlayClicked);
  },
  watch: {
    items: {
      handler() {
        this.$nextTick(() => {
          this.calculateVisibleItems();
        });
      },
      deep: true,
    },
    isMoreMenuOpen(isOpen) {
      // Only show overlay when opening - don't hide on close to avoid
      // race condition with other components that may be taking over the overlay
      if (isOpen) {
        this.setIsOverlayDisplay(true);
      }
    },
  },
  methods: {
    ...mapActions('AngularStore', ['setIsOverlayDisplay']),
    onOverlayClicked() {
      if (this.isMoreMenuOpen) {
        this.isMoreMenuOpen = false;
        this.setIsOverlayDisplay(false);
      }
    },
    initResizeObserver() {
      if (typeof ResizeObserver === 'undefined') {
        // Fallback for browsers without ResizeObserver
        window.addEventListener('resize', this.calculateVisibleItems);
        return;
      }

      this.resizeObserver = new ResizeObserver(() => {
        this.calculateVisibleItems();
      });

      if (this.$refs.navigationContainer) {
        this.resizeObserver.observe(this.$refs.navigationContainer);
      }
    },
    destroyResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      } else {
        window.removeEventListener('resize', this.calculateVisibleItems);
      }
    },
    calculateVisibleItems() {
      if (!this.$refs.navigationContainer || this.items.length === 0) {
        this.visibleCount = this.items.length;
        return;
      }

      const containerWidth = this.$refs.navigationContainer.offsetWidth;
      const moreButtonWidth = 100; // Estimated width for "More" button
      const itemEstimatedWidth = 120; // Estimated average item width
      const availableWidth = containerWidth - moreButtonWidth;

      // Calculate how many items can fit
      let count = Math.floor(availableWidth / itemEstimatedWidth);
      count = Math.max(1, Math.min(count, this.items.length));

      // If all items fit, show all
      if (count >= this.items.length) {
        this.visibleCount = this.items.length;
      } else {
        this.visibleCount = count;
      }
    },
    handleItemClick(item) {
      this.$emit('item-click', item);
    },
    handleOverflowItemSelect(identifier) {
      const listItem = this.overflowListItems.find((item) => item.identifier === identifier);
      if (listItem && listItem.originalItem) {
        this.isMoreMenuOpen = false;
        this.setIsOverlayDisplay(false);
        this.handleItemClick(listItem.originalItem);
      }
    },
    isItemExternal(item) {
      if (item.action) return isActionExternal(item);
      return isExternalUrl(item.url);
    },
  },
};
</script>

<style lang="scss" scoped>
.top-bar-navigation {
  display: flex;
  align-items: center;
  gap: var(--size-value1);
  flex: 1;
  min-width: 0;
  overflow: hidden;

  &__visible-items {
    display: flex;
    align-items: center;
    gap: var(--size-value1);
    flex-shrink: 0;
  }

  &__more-menu {
    flex-shrink: 0;
  }
}
</style>
